<script setup>
import { onMounted, ref } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useSettingsStore } from '@/stores/settings.js'
import ToastHost from '@/components/ui/ToastHost.vue'
import AppSpinner from '@/components/ui/AppSpinner.vue'

/**
 * The application root.
 *
 * Its only jobs are to hold the boot state, mount the toast host, and show a
 * thin progress bar during route transitions. Layout belongs to the layout
 * components; this one deliberately renders almost nothing.
 */
const auth = useAuthStore()
const settings = useSettingsStore()
const router = useRouter()

const isBooting = ref(true)
const routeProgress = ref(0)

// The router's first `beforeEach` already awaits the session restore, so this
// only has to wait for that navigation to settle.
router.isReady().finally(() => {
  isBooting.value = false
})

// A thin bar rather than a blocking spinner: lazily-loaded views usually
// arrive fast enough that a full-screen loader would only flash.
let progressTimer = null

router.beforeEach(() => {
  routeProgress.value = 18
  clearInterval(progressTimer)
  progressTimer = setInterval(() => {
    // Creeps toward 90% and waits there — the bar should never imply it is
    // finished before the view has actually loaded.
    routeProgress.value = Math.min(90, routeProgress.value + Math.random() * 14)
  }, 180)
})

router.afterEach(() => {
  clearInterval(progressTimer)
  routeProgress.value = 100
  setTimeout(() => (routeProgress.value = 0), 260)
})

onMounted(() => {
  if (!settings.isLoaded) settings.load()
  if (auth.isInitializing) auth.initialize()
})
</script>

<template>
  <div v-if="routeProgress > 0" class="progress-bar">
    <div class="progress-bar__fill" :style="{ width: `${routeProgress}%` }" />
  </div>

  <div v-if="isBooting" class="boot">
    <AppSpinner size="lg" label="Loading" />
  </div>

  <RouterView v-else />

  <ToastHost />
</template>

<style scoped lang="scss">
.boot {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  color: $pr-signal;
}
</style>
